import React, { useEffect, useState, useCallback } from "react";
import { stationsAPI } from "../../../shared/lib/api";
import { Input } from "../../../shared/components/ui/input";
import { cn } from "../../../shared/lib/utils";
import { Building2, Search, Loader2, MapPin } from "lucide-react";

/**
 * StationPicker - Typeahead search component for stations
 * 
 * @param {Object} props
 * @param {string} props.value - Currently selected station_code
 * @param {Function} props.onChange - Called with full station object when selected
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable the picker
 */
export default function StationPicker({ 
  value, 
  onChange, 
  className,
  disabled = false 
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);

  // Load initial station if value is provided
  useEffect(() => {
    if (value && !selectedStation) {
      loadStationByCode(value);
    }
  }, [value]);

  const loadStationByCode = async (code) => {
    try {
      const station = await stationsAPI.getByCode(code);
      if (station) {
        setSelectedStation(station);
        setQuery("");
      }
    } catch (err) {
      console.error("Failed to load station:", err);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }

    let alive = true;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await stationsAPI.search(query.trim());
        if (!alive) return;
        setResults(data?.results || []);
        setIsOpen(true);
      } catch (err) {
        if (alive) setResults([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  const handleSelect = useCallback(async (stationCode) => {
    try {
      const station = await stationsAPI.getByCode(stationCode);
      
      if (station) {
        setSelectedStation(station);
        setQuery("");
        setResults([]);
        setIsOpen(false);
        
        // Call onChange with full station data
        if (onChange) {
          onChange(station);
        }
      }
    } catch (err) {
      console.error("Failed to load station details:", err);
    }
  }, [onChange]);

  const handleClear = () => {
    setSelectedStation(null);
    setQuery("");
    setResults([]);
    if (onChange) {
      onChange(null);
    }
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay to allow click on results
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className={cn("relative", className)}>
      {/* Selected Station Display */}
      {selectedStation ? (
        <div 
          className="h-12 px-4 border-2 border-border bg-muted/30 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={disabled ? undefined : handleClear}
          data-testid="selected-station"
        >
          <div className="flex items-center gap-3">
            <Building2 className="h-4 w-4 text-[#FF6B00]" />
            <div>
              <span className="font-mono font-bold">
                {selectedStation.station_code && `${selectedStation.station_code} — `}
                {selectedStation.station_name}
              </span>
              {(selectedStation.city || selectedStation.state) && (
                <span className="text-muted-foreground text-sm ml-2">
                  ({selectedStation.city}{selectedStation.state ? `, ${selectedStation.state}` : ""})
                </span>
              )}
            </div>
          </div>
          {!disabled && (
            <span className="text-xs text-muted-foreground hover:text-foreground">
              Click to change
            </span>
          )}
        </div>
      ) : (
        /* Search Input */
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder="Type station name, code, or city..."
            className="h-12 pl-10 rounded-none border-2 font-mono"
            disabled={disabled}
            data-testid="station-search-input"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && !selectedStation && (
        <div 
          className="absolute z-50 w-full mt-1 border-2 border-border bg-background shadow-lg max-h-64 overflow-y-auto"
          data-testid="station-search-results"
        >
          {results.map((station) => (
            <button
              key={station.station_code || station.station_name}
              type="button"
              onClick={() => handleSelect(station.station_code)}
              className={cn(
                "w-full text-left px-4 py-3 border-b border-border last:border-b-0",
                "hover:bg-muted/50 transition-colors cursor-pointer",
                value === station.station_code && "bg-muted"
              )}
              data-testid={`station-option-${station.station_code}`}
            >
              <div className="font-mono font-bold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#FF6B00]" />
                {station.station_code && `${station.station_code} — `}
                {station.station_name}
              </div>
              {(station.city || station.state) && (
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {station.city}{station.state ? `, ${station.state}` : ""}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {isOpen && query.trim().length >= 2 && results.length === 0 && !loading && !selectedStation && (
        <div className="absolute z-50 w-full mt-1 border-2 border-border bg-background p-4 text-sm text-muted-foreground">
          No stations found matching "{query}"
        </div>
      )}
    </div>
  );
}
